import React, { useEffect, useState } from 'react';
import { Text } from '@gjensidige/builders-components';
import {
  CalculationWidget,
  CalculationWidgetProps
} from '@gjensidige/se-common-calculation';
import { validateSSN } from '@gjensidige/se-helpers';

import RegNrImage from '../images/reg-nr-image.svg';
import { AppHelper } from '../utils/AppHelper';
import { ageResolver } from '../utils/CalculationHelper';

interface WidgetProps {
  enonicData: {
    headerText: string;
    imageAltText: string;
    regNumberLabel: string;
    regNumberPlaceHolder: string;
    cprNumberLabel: string;
    cprNumberPlaceHolder: string;
    cprNumberHelpText: string;
    calculateBtnLabel: string;
    footerMessage: string;
    imageLocations: {
      image: string;
      smallScreenImage: string;
      regPlateImage: string;
      hideImageOnSmallScreens: boolean;
    };
  };
}
export const InputWidgetContainer: React.FC<WidgetProps> = ({
  enonicData
}: WidgetProps) => {
  const [registrationNumber, setRegistrationNumber] = useState('');
  const [personNumber, setPersonNumber] = useState('');
  const [enableButton, setEnableButton] = useState(false);
  const [validRegNumber, isValidRegNo] = useState(false);
  const [validPersonNumber, isValidPersonNumber] = useState(false);
  const [regNumberErrorText, setRegNumberErrorText] = useState('');
  const [personNumberErrorText, setPersonNumberErrorText] = useState('');

  const MIN_YEAR_PREFIX = 19;
  const MIN_DRIVER_AGE = 18;
  const NEXT_PAGE_URL_PATH = '/links/webshop-car';
  useEffect(() => {
    setEnableButton(validRegNumber && validPersonNumber);
  }, [validRegNumber, validPersonNumber]);

  const handleOnChangePersonNumber = (event: any) => {
    event.preventDefault();
    const customerInput = event.currentTarget.value.replace(/\D/g, '');
    setPersonNumber(customerInput.toUpperCase());
    const isPersonNumberValid = cprAndAgeValidator(
      { age: MIN_DRIVER_AGE },
      customerInput
    );
    isValidPersonNumber(isPersonNumberValid);
    if (!customerInput.length) {
      setPersonNumberErrorText('');
    }
  };

  const handleOnBlurPersonNumber = (event: any) => {
    event.preventDefault();
    const customerInput = event.currentTarget.value.replace(/\D/g, '');
    if (!customerInput.length) {
      setPersonNumberErrorText('');
    } else {
      if (validPersonNumber) {
        setPersonNumberErrorText('');
      } else {
        if (!validateSSN(customerInput)) {
          setPersonNumberErrorText(AppHelper.getText('cprInvalidMessage'));
        } else if (ageResolver(customerInput) < MIN_DRIVER_AGE) {
          setPersonNumberErrorText(AppHelper.getText('ageInvalidMessage'));
        }
      }
    }
  };

  const handleOnChangeRegNo = (event: any) => {
    event.preventDefault();
    const customerInput = event.currentTarget.value.trim().toUpperCase();

    const validRegNumber = new RegExp(/([A-Z]{3})([0-9]{2})([0-9A-Z])/).test(
      customerInput
    );
    const finalValidRegNumber = customerInput.length === 6 && validRegNumber;
    setRegistrationNumber(customerInput.toUpperCase());
    isValidRegNo(finalValidRegNumber);
    if (!customerInput.length) {
      setRegNumberErrorText('');
    }
  };

  const handleOnBlurRegNo = (event: any) => {
    event.preventDefault();
    const customerInput = event.currentTarget.value.trim().toUpperCase();
    if (!customerInput.length) {
      setRegNumberErrorText('');
    } else {
      if (validRegNumber) {
        setRegNumberErrorText('');
      } else {
        setRegNumberErrorText(AppHelper.getText('regNumberInvalidMessage'));
      }
    }
  };

  const getCprMoreInfoContent = () => {
    return (
      <Text>
        Vi behöver ditt personnummer för att kunna ge dig rätt pris.
        Personnumret ger oss bland annat uppgifter om var du bor och din ålder.
      </Text>
    );
  };

  const cprAndAgeValidator = (options: any, inputValue: any) => {
    if (
      (inputValue.length !== 10 && inputValue.length !== 12) ||
      (inputValue.length === 12 &&
        Number(inputValue.slice(0, 2)) < MIN_YEAR_PREFIX) ||
      !validateSSN(inputValue)
    ) {
      return false;
    } else {
      if (ageResolver(inputValue) < options.age) {
        return false;
      } else return true;
    }
  };

  const gotoNextStep = (event: any) => {
    event.preventDefault();
    const customerInfo = {
      cprNumber: personNumber,
      registrationNumber: registrationNumber
    };
    sessionStorage.setItem('customerInfo', JSON.stringify(customerInfo));
    if (NEXT_PAGE_URL_PATH) {
      window.location.href = window.location.origin + NEXT_PAGE_URL_PATH;
    }
  };
  const carParams: CalculationWidgetProps = {
    headerText: enonicData.headerText || AppHelper.getText('car.widgetTitle'),
    image: enonicData.imageLocations.image,
    smallScreenImage: enonicData.imageLocations.smallScreenImage,
    imageAltText: enonicData.imageAltText || '',
    hideImageOnSmallScreens: enonicData.imageLocations.hideImageOnSmallScreens,
    inputLists: {
      calculationInputs: [
        {
          type: 'inputText',
          inputListInRow: [
            {
              id: 'registration-number',
              labelText:
                enonicData.regNumberLabel || AppHelper.getText('regInputLabel'),
              name: 'input-registrationNumber',
              onChange: handleOnChangeRegNo,
              onBlur: handleOnBlurRegNo,
              value: registrationNumber,
              className: '',
              labelClassName: 'input-label-text',
              inputWrapperClassName: 'reg-nr',
              invalid: !validRegNumber && !!regNumberErrorText,
              errorText: regNumberErrorText,
              prefixImage: <img src={RegNrImage} alt="Registreringsnummer" />
            }
          ]
        },
        {
          type: 'inputText',
          inputListInRow: [
            {
              id: 'cpr-number',
              labelText:
                enonicData.cprNumberLabel || AppHelper.getText('cprLabelText'),
              name: 'input-cprNumber',
              onChange: handleOnChangePersonNumber,
              onBlur: handleOnBlurPersonNumber,
              value: personNumber,
              className: 'mt-1',
              labelClassName: 'input-label-text',
              inputWrapperClassName: 'ssn',
              invalid: !validPersonNumber && !!personNumberErrorText,
              errorText: personNumberErrorText,
              helpText: '12 siffror ÅÅÅÅMMDDNNNN.',
              isHelpTextAlwaysVisible: true,
              inputMode: 'numeric',
            }
          ]
        }
      ],
      calculateBtn: {
        btnText:
          enonicData.calculateBtnLabel ||
          AppHelper.getText('calculateBtnLabel'),
        onClick: gotoNextStep,
        enableTracking: true,
        disabled: !enableButton,
        className: 'calculate-price mt-2'
      }
    },
    additionalInfoLinkText: AppHelper.getText(
      'personalDataHandlingInfoLinkText'
    ),
    additionalInfoLink: AppHelper.getText('personalDataHandlinkUrl'),
    footerMessage: enonicData.footerMessage || ''
  };

  return <CalculationWidget {...carParams} />;
};
